 import React, { useEffect, useState } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { MoreHorizontal } from "lucide-react";
import Button from "../components/ui/Button";
import DataTable, { Column } from "../components/ui/DataTable";
import { fetchRoles, RoleApiItem, deleteRoleById } from "../services/groupsService";
import RolesDetailsDrawer from "../components/RolesDetailsDrawer";
import { formatPermission } from "../lib/utils";
import { showConfirmDeleteModal, showErrorModal } from "../components/ui/ConfirmDialog";
import ForbiddenState from "../components/ui/ForbiddenState";
import { isAxiosForbidden } from "../lib/utils";
import { useSecurity } from "../security/SecurityContext";
 

// Use the API type directly
type GroupRow = RoleApiItem;

// Initial empty state - data will be loaded from API
const initialGroups: GroupRow[] = [];

export default function Roles() {
  const { has } = useSecurity();
  const canManageRoles = has("Manage_Roles");
  const [rows, setRows] = useState<GroupRow[]>(initialGroups);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalGroups, setTotalGroups] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);

  // Row actions menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);
  const openMenu = Boolean(menuAnchorEl);
  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>, rowId: string) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuRowId(rowId);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuRowId(null);
  };

  const openCreateDrawer = () => {
    setEditingGroupId(null);
    setDrawerOpen(true);
  };
  const openEditDrawer = (id: string) => {
    setEditingGroupId(id);
    setDrawerOpen(true);
  };
  const closeDrawer = () => setDrawerOpen(false);

  // Pagination state (local, demo)
  const [page, setPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(50);
  const [totalResults, setTotalResults] = useState<number>(rows.length);
  const [forbidden, setForbidden] = useState<boolean>(false);

  useEffect(() => {
    const loadGroups = async () => {
      try {
        setLoading(true);
        setForbidden(false);
        const response = await fetchRoles({
          page: page,
          pageSize: itemsPerPage
        });
        setRows(response.results);
        setTotalGroups(response.totalCount);
        setTotalResults(response.totalResults);
        // Note: activeUsers is not provided by the roles API, so we'll keep it as null
      } catch (error) {
        if (isAxiosForbidden(error)) {
          setForbidden(true);
        } else {
          console.error('Failed to fetch roles:', error);
        }
        setRows([]);
        setTotalGroups(null);
        setTotalResults(0);
      } finally {
        setLoading(false);
      }
    };

    loadGroups();
  }, [page, itemsPerPage]);

  const columns: Column<GroupRow>[] = [
    {
      id: 'name', header: 'Role Name', width: '20%', align: 'left', cell: (g: GroupRow) => (
        <span>{g.name}</span>
      )
    },
    { id: 'description', header: 'Description', width: '25%', align: 'left', cell: (g: GroupRow) => g.description || "--" },
    { id: 'userCount', header: 'User Count', width: '15%', align: 'center', cell: (g: GroupRow) => (
      <span className="font-semibold text-gray-900">{g.userCount}</span>
    ) },
    {
      id: 'perm', header: 'Permissions', width: '30%', align: 'left', cell: (g: GroupRow) => (
        <div className="flex flex-wrap gap-1">
          {g.permissions.length > 0 ? (
            g.permissions.map(p => (
              <span key={p} className="text-[11px] px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-gray-700">{formatPermission(p)}</span>
            ))
          ) : (
            <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-gray-700">--</span>
          )}
        </div>
      )
    },
    {
      id: 'actions', header: 'Actions', width: '10%', align: 'center', cell: (g: GroupRow) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e as any, g.id)}>
          <MoreHorizontal className="h-4 w-4 text-gray-500" />
        </IconButton>
      )
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#F7F7F7]">
        <div className="max-w-screen-2xl mx-auto px-4 py-4 flex items-center">
          <h1 className="text-xl font-semibold text-gray-900">Roles</h1>
          <div className="ml-6 flex items-center gap-8 text-sm">
            <div className="flex flex-col">
              <span className="text-gray-500">Total Roles</span>
              <span className="text-gray-900 font-semibold">{typeof totalGroups === 'number' ? totalGroups : '--'}</span>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Button
              variant="primary"
              onClick={canManageRoles ? openCreateDrawer : undefined}
              disabled={!canManageRoles}
              title={canManageRoles ? undefined : "You don't have permission to manage roles"}
            >
              Create New Role
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="p-6">
        <div className="max-w-screen-2xl mx-auto">
          <DataTable<GroupRow>
            className=""
            loading={loading}
            rows={rows}
            columns={columns}
            page={page}
            totalPages={Math.max(1, Math.ceil(totalResults / itemsPerPage))}
            onPageChange={(p) => setPage(p)}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); setPage(1); }}
            totalResults={totalResults}
            emptyRender={forbidden ? (
              <ForbiddenState />
            ) : undefined}
          />
          <Menu anchorEl={menuAnchorEl} open={openMenu} onClose={handleMenuClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            {menuRowId && (
              <>
                <MenuItem onClick={() => { const id = menuRowId; handleMenuClose(); openEditDrawer(id); }}>Edit Role</MenuItem>
                <MenuItem onClick={async () => {
                  const id = menuRowId;
                  handleMenuClose();

                  // Find the group to check user count
                  const groupToDelete = rows.find(g => g.id === id);
                  if (groupToDelete && groupToDelete.userCount > 0) {
                    await showErrorModal({
                      title: 'Cannot Delete Role',
                      description: 'Roles with assigned users cannot be deleted. Please remove all users from this role before deleting it.',
                      confirmText: 'OK',
                    });
                    return;
                  }

                  const confirmed = await showConfirmDeleteModal({ entityLabel: 'Role' });
                  if (!confirmed) return;
                  try {
                    setLoading(true);
                    await deleteRoleById(id);
                    const response = await fetchRoles({ page, pageSize: itemsPerPage });
                    setRows(response.results);
                    setTotalGroups(response.totalCount);
                    setTotalResults(response.totalResults);
                  } catch (err) {
                    console.error('Failed to delete role', err);
                  } finally {
                    setLoading(false);
                  }
                }}>Delete Role</MenuItem>
              </>
            )}
          </Menu>
          <RolesDetailsDrawer
            open={drawerOpen}
            onClose={closeDrawer}
            groupId={editingGroupId}
            onSaved={() => {
              // reload current page
              (async () => {
                try {
                  setLoading(true);
                  const response = await fetchRoles({ page, pageSize: itemsPerPage });
                  setRows(response.results);
                  setTotalGroups(response.totalCount);
                  setTotalResults(response.totalResults);
                } catch (err) {
                  console.error('Failed to refresh roles:', err);
                } finally {
                  setLoading(false);
                }
              })();
            }}
          />
        </div>
      </div>
    </div>
  );
}


